import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { invokeEdgeFunction } from './supabaseEdge'

/**
 * Loads role-scoped platform overview aggregates from the platform-overview edge function.
 * Unauthorized widget keys are omitted by the server — absence ≠ zero.
 */
export function usePlatformOverviewData() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [lastUpdated, setLastUpdated] = useState(null)
  const [generatedAt, setGeneratedAt] = useState(null)
  const [role, setRole] = useState(null)
  const [widgets, setWidgets] = useState({})
  const [modules, setModules] = useState([])

  const loadInFlightRef = useRef(false)
  const pendingReloadRef = useRef(false)
  const loadGenerationRef = useRef(0)

  const load = useCallback(async () => {
    if (loadInFlightRef.current) {
      pendingReloadRef.current = true
      return
    }
    loadInFlightRef.current = true
    const generation = ++loadGenerationRef.current

    setLoading(true)
    setError('')
    try {
      const res = await invokeEdgeFunction('platform-overview', { method: 'GET' })
      const body = await res.json().catch(() => ({}))
      if (generation !== loadGenerationRef.current) return
      if (!res.ok) {
        throw new Error(body.error || body.message || `Overview failed (${res.status})`)
      }
      setWidgets(body.widgets && typeof body.widgets === 'object' ? body.widgets : {})
      setModules(Array.isArray(body.modules) ? body.modules : [])
      setGeneratedAt(body.generated_at || null)
      setRole(body.role || null)
      setLastUpdated(new Date())
    } catch (e) {
      if (generation === loadGenerationRef.current) {
        setError(e?.message || String(e))
      }
    } finally {
      loadInFlightRef.current = false
      if (pendingReloadRef.current) {
        pendingReloadRef.current = false
        void load()
      } else if (generation === loadGenerationRef.current) {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    void load()
    return () => {
      loadGenerationRef.current += 1
    }
  }, [load])

  const clientStats = widgets.clients ?? null
  const companySplit = widgets.analytics?.byCompany ?? null
  const alertCount = widgets.adimpact?.alertClients ?? null
  const resourceCount = widgets.resources?.links ?? null
  const raceStats = widgets.raceTracker
    ? {
        totalRaces: widgets.raceTracker.totalRaces,
        activeRaces: widgets.raceTracker.activeRaces,
        totalAdvertisers: widgets.raceTracker.totalAdvertisers,
        smgPlus: widgets.raceTracker.smgPlus,
        digitalOverdue: widgets.raceTracker.digitalOverdue,
        linearOverdue: widgets.raceTracker.linearOverdue,
      }
    : null
  const bplStats = widgets.bigPoliticalList ?? null
  const ideaBoardStats = widgets.ideaBoard ?? null
  const knowledgeSearch = widgets.knowledgeSearch ?? null
  const personalLibrary = widgets.personalLibrary ?? null
  const linkedProjectCount = widgets.basecamp?.linkedProjects ?? null
  const syncLogs = widgets.basecamp?.syncLogs ?? []
  const agentConfigs = widgets.aiAgents?.agents ?? []
  const cronJobs = widgets.aiOps?.cronJobs ?? []
  const cronGroups = widgets.aiOps?.recentRunsSummary ?? []

  const digestCronJob = useMemo(
    () => cronJobs.find((j) => j.jobname === 'basecamp-agents-digest-daily') ?? null,
    [cronJobs]
  )

  const syncCronJobs = useMemo(
    () =>
      [...cronJobs]
        .filter((j) => j.jobname !== 'basecamp-agents-digest-daily')
        .sort((a, b) => String(a.jobname).localeCompare(String(b.jobname))),
    [cronJobs]
  )

  return {
    loading,
    error,
    lastUpdated,
    generatedAt,
    role,
    refresh: load,
    widgets,
    modules,
    clientStats,
    alertCount,
    resourceCount,
    raceStats,
    bplStats,
    ideaBoardStats,
    knowledgeSearch,
    personalLibrary,
    cronJobs,
    syncCronJobs,
    digestCronJob,
    syncLogs,
    cronGroups,
    agentConfigs,
    linkedProjectCount,
    companySplit,
    /** True when personal library feature is on for this user (payload present). */
    personalLibraryEnabled: Boolean(widgets.personalLibrary),
  }
}
